/* ==========================================================================
   SUPERDUPER HOBO — shared runtime (sub-nav + dialog drawer).
   Loaded as <script type="module" src="{{ 'sd-hobo.js' | asset_url }}"> by the
   sub-nav, landing sections, size guide and the HOBO PDP. Module scripts with
   the same URL run once; the window guard covers a copy loaded another way.
   Everything is delegated on document, so sections re-rendered by the theme
   editor (shopify:section:load) need no re-binding; the sub-nav measure and
   its observer are rebuilt on section load/unload.

   Contracts (used by the section files):
   - Sub-nav: [data-hb-subnav] (the bar), [data-hb-subnav-link] (a[href="#id"]).
     Its height goes to --hb-subnav-h on <html> (0px when the bar is not sticky,
     e.g. hb-subnav--static-mobile below 768px). The link whose target
     ([data-hb-anchor] or any element with that id) sits under the bar gets
     .is-active + aria-current="true".
   - Dialog: <dialog class="sd-hobo-dialog" id="x">. Opened by any
     [data-hb-dialog-open="x"] or a[href="#x"]; closed by [data-hb-dialog-close],
     a click on the backdrop, Escape (native), eventBus 'close:all:drawers'.
     A .sd-hobo-dialog--lightbox closes on any click that is not on a control.
     While open: body.js-drawer-open (theme scroll lock), added only if the body
     did not already have it and removed only if this code added it (a theme
     drawer opened meanwhile keeps its lock); focus goes back to the opener.
     Opening emits 'close:all:drawers' so theme drawers close.
     Theme editor: selecting the section (or a block of it) opens its dialog,
     deselecting closes it.
   - API: window.sdHobo.openDialog(idOrEl, opener?), window.sdHobo.closeDialog(idOrEl),
     window.sdHobo.refresh() (re-measure the sub-nav).
   ========================================================================== */
(() => {
  if (window.__sdHoboInit) return;
  window.__sdHoboInit = true;

  const docEl = document.documentElement;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ---------- eventBus (eventBus.js is deferred in <head>) ---------- */
  const whenBus = (cb) => {
    if (window.eventBus) { cb(window.eventBus); return; }
    let tries = 0;
    const retry = () => {
      if (window.eventBus) { cb(window.eventBus); return; }
      if (++tries < 40) setTimeout(retry, 250);
    };
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', retry, { once: true });
    } else {
      retry();
    }
  };
  const emit = (name, data) => {
    if (window.eventBus && typeof window.eventBus.emit === 'function') window.eventBus.emit(name, data);
  };

  const px = (name) => {
    const v = parseFloat(getComputedStyle(docEl).getPropertyValue(name));
    return Number.isFinite(v) ? v : 0;
  };
  const headerHeight = () => px('--sticky-header-desktop-height') + px('--sticky-header-mobile-height');

  /* ======================================================================
     Sub-nav
     ====================================================================== */
  let subnav = null;
  let subnavRO = null;
  let anchorIO = null;
  let subnavH = -1;
  let linkByTarget = new Map();
  const visible = new Set();

  const isSticky = (el) => {
    const host = el.closest('.shopify-section') || el;
    const pos = getComputedStyle(host).position;
    return pos === 'sticky' || pos === 'fixed';
  };

  const measure = () => {
    let h = 0;
    if (subnav && subnav.isConnected && isSticky(subnav)) {
      h = Math.round(subnav.getBoundingClientRect().height);
    }
    if (h !== subnavH) {
      subnavH = h;
      docEl.style.setProperty('--hb-subnav-h', `${h}px`);
      buildObserver();
    }
  };

  const setActive = (link) => {
    if (!subnav) return;
    subnav.querySelectorAll('[data-hb-subnav-link]').forEach((a) => {
      const on = a === link;
      a.classList.toggle('is-active', on);
      if (on) a.setAttribute('aria-current', 'true');
      else a.removeAttribute('aria-current');
    });
    if (!link) return;
    const strip = link.parentElement;
    if (strip && strip.scrollWidth > strip.clientWidth + 1) {
      const left = link.offsetLeft - (strip.clientWidth - link.offsetWidth) / 2;
      strip.scrollTo({ left: Math.max(0, left), behavior: reduceMotion.matches ? 'auto' : 'smooth' });
    }
  };

  const pickActive = () => {
    let active = null;
    // last visible target in document order wins
    linkByTarget.forEach((link, target) => {
      if (visible.has(target)) active = link;
    });
    setActive(active);
  };

  function buildObserver() {
    if (anchorIO) { anchorIO.disconnect(); anchorIO = null; }
    visible.clear();
    linkByTarget = new Map();
    if (!subnav || !subnav.isConnected || !('IntersectionObserver' in window)) return;

    const pairs = [];
    subnav.querySelectorAll('[data-hb-subnav-link]').forEach((a) => {
      const href = a.getAttribute('href') || '';
      if (!href.startsWith('#') || href.length < 2) return;
      let target = null;
      try { target = document.getElementById(decodeURIComponent(href.slice(1))); } catch (e) { target = null; }
      if (target) pairs.push([target, a]);
    });
    if (!pairs.length) return;
    pairs.sort((x, y) => (x[0].compareDocumentPosition(y[0]) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1));
    pairs.forEach(([t, a]) => linkByTarget.set(t, a));

    // A 1px line just under header + sub-nav: the target crossing it is the current one.
    const top = Math.round(headerHeight() + Math.max(subnavH, 0) + 12);
    const bottom = Math.max(0, window.innerHeight - top - 1);
    anchorIO = new IntersectionObserver((entries) => {
      entries.forEach((en) => {
        if (en.isIntersecting) visible.add(en.target);
        else visible.delete(en.target);
      });
      pickActive();
    }, { rootMargin: `-${top}px 0px -${bottom}px 0px`, threshold: 0 });
    linkByTarget.forEach((_, t) => anchorIO.observe(t));
  }

  const initSubnav = () => {
    const found = document.querySelector('[data-hb-subnav]');
    if (subnavRO) { subnavRO.disconnect(); subnavRO = null; }
    subnav = found;
    subnavH = -1;
    if (!subnav) {
      docEl.style.setProperty('--hb-subnav-h', '0px');
      if (anchorIO) { anchorIO.disconnect(); anchorIO = null; }
      return;
    }
    if ('ResizeObserver' in window) {
      subnavRO = new ResizeObserver(() => measure());
      subnavRO.observe(subnav);
    }
    measure();
    buildObserver();
  };

  // Heroes that must end at the fold ([data-hb-fit]): distance from the document top, set before first paint
  // by an inline script in the section, refreshed here on resize (announcement bar / header can wrap).
  const fitHeroes = () => {
    document.querySelectorAll('[data-hb-fit]').forEach((el) => {
      const t = Math.round(el.getBoundingClientRect().top + window.pageYOffset);
      if (t >= 0 && t < window.innerHeight * 0.6) el.style.setProperty('--hb-fit-top', `${t}px`);
      else el.style.removeProperty('--hb-fit-top');
    });
  };

  let resizeTimer = 0;
  const onResize = () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => { subnavH = -1; measure(); fitHeroes(); }, 120);
  };
  window.addEventListener('resize', onResize, { passive: true });
  // the theme header changes height when it sticks / unsticks
  document.addEventListener('headerSticked', onResize);
  document.addEventListener('headerUnstuck', onResize);

  document.addEventListener('click', (e) => {
    const link = e.target.closest && e.target.closest('[data-hb-subnav-link]');
    if (link && subnav && subnav.contains(link)) setActive(link);
  });

  /* ======================================================================
     Dialog drawer
     ====================================================================== */
  const openers = new WeakMap();

  const getDialog = (ref) => {
    const el = typeof ref === 'string' ? document.getElementById(ref) : ref;
    return el && el.tagName === 'DIALOG' && el.classList.contains('sd-hobo-dialog') ? el : null;
  };

  const anyOpen = () => !!document.querySelector('dialog.sd-hobo-dialog[open]');

  // Scroll lock (body.js-drawer-open is shared with the theme drawers: cart, size guide, offers,
  // store availability, mobile menu, filters). We remove it on close only if THIS code added it
  // (lockOwned) and no theme drawer is open at that moment: a theme drawer opened while a HOBO
  // dialog was open (it emits close:all:drawers, then adds the class, which is already there)
  // keeps its lock when our async 'close' event lands afterwards.
  let lockOwned = false;
  const THEME_DRAWERS_OPEN = 'cart-drawer[aria-hidden="false"], .global-drawer[aria-hidden="false"], size-guide-drawer[aria-hidden="false"], offers-drawer[aria-hidden="false"], store-availability-drawer[aria-hidden="false"]';
  const themeDrawerOpen = () => {
    const b = document.body;
    return b.classList.contains('js-drawer-open-left') || b.classList.contains('js-drawer-open-filter') ||
      !!document.querySelector(THEME_DRAWERS_OPEN);
  };
  const lock = () => {
    const b = document.body;
    if (b.classList.contains('js-drawer-open')) return; // someone else holds it: leave ownership to them
    b.classList.add('js-drawer-open');
    lockOwned = true;
  };
  const unlock = () => {
    if (anyOpen()) return;
    if (lockOwned && !themeDrawerOpen()) document.body.classList.remove('js-drawer-open');
    lockOwned = false;
  };

  const openDialog = (ref, opener) => {
    const dialog = getDialog(ref);
    if (!dialog || dialog.open) return dialog;
    emit('close:all:drawers', dialog);
    openers.set(dialog, opener || document.activeElement);
    if (typeof dialog.showModal === 'function') {
      try { dialog.showModal(); } catch (err) { dialog.setAttribute('open', ''); }
    } else {
      dialog.setAttribute('open', '');
    }
    lock();
    dialog.dispatchEvent(new CustomEvent('hb:dialog:open', { bubbles: true }));
    return dialog;
  };

  const closeDialog = (ref) => {
    const dialog = getDialog(ref);
    if (!dialog || !dialog.open) return;
    if (typeof dialog.close === 'function') dialog.close();
    else { dialog.removeAttribute('open'); onClosed(dialog); }
  };

  const onClosed = (dialog) => {
    unlock();
    const opener = openers.get(dialog);
    openers.delete(dialog);
    if (opener && opener.isConnected && typeof opener.focus === 'function') {
      try { opener.focus({ preventScroll: true }); } catch (err) { /* noop */ }
    }
    dialog.dispatchEvent(new CustomEvent('hb:dialog:close', { bubbles: true }));
  };

  // native 'close' (Escape, dialog.close()) does not bubble: capture it on document
  document.addEventListener('close', (e) => {
    const d = e.target;
    if (d && d.tagName === 'DIALOG' && d.classList.contains('sd-hobo-dialog')) onClosed(d);
  }, true);

  const isControl = (el) => !!(el && el.closest && el.closest('a, button, input, select, textarea, label, [role="button"]'));

  document.addEventListener('click', (e) => {
    const t = e.target;
    if (!t || !t.closest) return;

    // close buttons
    const closer = t.closest('[data-hb-dialog-close]');
    if (closer) {
      const d = closer.closest('dialog.sd-hobo-dialog');
      if (d) { e.preventDefault(); closeDialog(d); return; }
    }

    // inside an open dialog: backdrop click / lightbox click
    const inDialog = t.closest('dialog.sd-hobo-dialog');
    if (inDialog) {
      if (t === inDialog) {
        const r = inDialog.getBoundingClientRect();
        const outside = e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom;
        // clicks with no coordinates (keyboard) never close
        if (outside && (e.clientX || e.clientY)) { closeDialog(inDialog); return; }
      }
      if (inDialog.classList.contains('sd-hobo-dialog--lightbox') && !isControl(t)) {
        closeDialog(inDialog);
        return;
      }
    }

    // openers
    const opener = t.closest('[data-hb-dialog-open], a[href^="#"]');
    if (!opener) return;
    let id = opener.getAttribute('data-hb-dialog-open');
    if (!id && opener.tagName === 'A') {
      const href = opener.getAttribute('href') || '';
      if (href.length > 1) id = href.slice(1);
    }
    if (!id) return;
    const dialog = getDialog(id);
    if (!dialog) return;
    e.preventDefault();
    openDialog(dialog, opener);
  });

  whenBus((bus) => {
    bus.on('close:all:drawers', (source) => {
      document.querySelectorAll('dialog.sd-hobo-dialog[open]').forEach((d) => {
        if (d !== source) closeDialog(d);
      });
    });
  });

  /* ======================================================================
     Theme editor
     ====================================================================== */
  const designMode = () => !!(window.Shopify && window.Shopify.designMode);
  const dialogsIn = (sectionEl, sectionId) => {
    const list = new Set();
    if (sectionEl && sectionEl.querySelectorAll) {
      sectionEl.querySelectorAll('dialog.sd-hobo-dialog').forEach((d) => list.add(d));
    }
    if (sectionId) {
      document.querySelectorAll('dialog.sd-hobo-dialog[data-section-id]').forEach((d) => {
        if (d.getAttribute('data-section-id') === String(sectionId)) list.add(d);
      });
    }
    return [...list];
  };
  const sectionIdOf = (e) => (e.detail && e.detail.sectionId) || '';

  document.addEventListener('shopify:section:select', (e) => {
    if (!designMode()) return;
    dialogsIn(e.target, sectionIdOf(e)).forEach((d) => openDialog(d));
  });
  document.addEventListener('shopify:block:select', (e) => {
    if (!designMode()) return;
    const sectionEl = document.getElementById(`shopify-section-${sectionIdOf(e)}`);
    dialogsIn(sectionEl, sectionIdOf(e)).forEach((d) => openDialog(d));
  });
  document.addEventListener('shopify:section:deselect', (e) => {
    if (!designMode()) return;
    dialogsIn(e.target, sectionIdOf(e)).forEach((d) => closeDialog(d));
  });
  document.addEventListener('shopify:section:unload', (e) => {
    dialogsIn(e.target, sectionIdOf(e)).forEach((d) => closeDialog(d));
    // the unloaded section may be the sub-nav itself: rebuild after it is gone
    setTimeout(() => { initSubnav(); unlock(); }, 0);
  });
  document.addEventListener('shopify:section:load', () => {
    initSubnav();
  });
  document.addEventListener('shopify:section:reorder', () => {
    initSubnav();
  });

  /* ---------- public API + boot ---------- */
  window.sdHobo = Object.assign(window.sdHobo || {}, {
    openDialog,
    closeDialog,
    refresh: () => { subnavH = -1; initSubnav(); }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initSubnav, { once: true });
  } else {
    initSubnav();
  }
  // theme.min.js (deferred) writes the header height vars after us: re-measure once everything settled
  window.addEventListener('load', () => { subnavH = -1; measure(); buildObserver(); }, { once: true });
})();
